/* eslint-disable @typescript-eslint/naming-convention */

/**
 * Plany abonamentowe drzew. Definicje (limity, ceny) mieszkają w tabeli plan
 * po stronie backendu - frontend tylko je wyświetla. Nazwy planów są kluczami
 * tłumaczeń (plan.free, plan.premium, ...), wzorem Globals.adminLevels.
 *
 * Cenę pokazujemy z katalogu Paddle (SubscriptionService.localizedPlanPrices);
 * price_cents + price_currency to zapas na wypadek, gdyby Paddle.js nie
 * wystartował - backend trzyma go zgodnym z katalogiem przez webhook
 * price.updated. Cennik jest jednolity dla całego świata (euro).
 */

/** Definicja planu z cennika (GET /subscription/plans) */
export interface Plan {
    code: PlanCode;
    sort: number;
    /** null = bez limitu */
    person_limit: number | null;
    /** null = bez limitu; string, bo bigint przychodzi jako tekst */
    storage_limit_bytes: string | null;
    pdf_export: boolean;
    /** Czy plan daje wyszukiwanie pokrewieństwa (plan Podstawowy nie daje) */
    relation_search: boolean;
    /** Czy plan pozwala zapraszać współpracowników do drzewa */
    invite_users: boolean;
    /** Czy plan jest płatny (ma cenę w katalogu Paddle) */
    paid: boolean;
    /** Cena roczna w najmniejszej jednostce waluty - zapas, gdy Paddle milczy */
    price_cents: number | null;
    /** Waluta ceny zapasowej (ISO 4217, np. 'EUR'); null dla planu darmowego */
    price_currency: string | null;
    /** Roczna cena Paddle (pri_...); null = plan darmowy albo katalog niepodpięty */
    paddle_price_id?: string | null;
}

export type PlanCode = 'free' | 'premium' | 'premium_plus' | 'ultra';

/** Plan efektywny drzewa - dołączany do payloadu drzewa i listy drzew konta */
export interface TreePlanInfo {
    code: PlanCode;
    person_limit: number | null;
    storage_limit_bytes: string | null;
    pdf_export: boolean;
    relation_search: boolean;
    invite_users: boolean;
    status: 'active' | 'past_due' | 'canceled';
    /** null = bezterminowo (plan darmowy albo nadanie ręczne) */
    valid_to: string | null;
    cancel_at_period_end: boolean;
    /** paddle | paddle_onetime | manual; null dla planu darmowego */
    source: string | null;
}

/** Zużycie drzewa liczone przez backend (osoby + bajty plików) */
export interface TreeUsage {
    persons: number;
    /** bigint jako tekst */
    storage_bytes: string;
}

/** Odpowiedź GET /subscription/tree/{id} */
export interface TreeSubscriptionInfo {
    plan: TreePlanInfo;
    usage: TreeUsage;
    /**
     * Identyfikator kupującego w Paddle (ctm_...); null, gdy drzewo nigdy
     * niczego nie kupiło. Potrzebny Paddle Retain - patrz
     * SubscriptionService.setRetainCustomer(). To identyfikator PADDLE,
     * nie nasze user.id ani adres e-mail.
     */
    paddle_customer_id?: string | null;
}

/** Klucze tłumaczeń nazw planów, indeksowane kodem planu */
export const PLAN_NAME_KEYS: Record<PlanCode, string> = {
    free: 'plan.free',
    premium: 'plan.premium',
    premium_plus: 'plan.premiumPlus',
    ultra: 'plan.ultra'
};
